import { colors } from '@lmc/common/constants'
import { Button, Spinner } from '@lmc/ui-kit'
import type React from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'

type OldButtonType =
  | 'add'
  | 'back'
  | 'cancel'
  | 'confirm'
  | 'delete'
  | 'draft'
  | 'edit'
  | 'primary'
  | 'resend'
  | 'success'

type OldButtonProps = {
  children?: React.ReactNode
  className?: string
  disabled?: boolean
  href?: string
  id?: string
  link?: string
  loading?: boolean
  onClick?: () => void
  style?: React.CSSProperties
  target?: string
  text?: string
  type?: OldButtonType
}

const boxShadow = '0 2px 4px rgba(34,34,34,0.15)'
const boxShadowLight = '0 2px 4px rgba(34,34,34,0.05)'

const commonStyles = css`
  border: none;
  color: ${colors.black};
  text-shadow: none !important;
  transition: all 0.3s ease;
`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StyledButton = styled(Button as any)<{
  $type: OldButtonType
  $disabled?: boolean
}>`
  ${commonStyles}
  ${({ $type, $disabled }: { $type: OldButtonType; $disabled?: boolean }) => {
    switch ($type) {
      case 'add':
        return css`
          background: ${colors.successgreen} !important;
          color: white !important;
          &:hover {
            background: ${colors.successgreen} !important;
            box-shadow: ${boxShadow};
            color: white !important;
          }
          &:focus {
            color: white !important;
            box-shadow: ${boxShadowLight};
          }
        `
      case 'back':
        return css`
          background: Transparent;
          color: ${colors.red};
          &:hover {
            color: ${colors.red};
          }
        `
      case 'cancel':
        return css`
          background: ${colors.grey};
          &:hover {
            background: ${colors.lightgrey};
            box-shadow: ${boxShadow};
          }
          &:focus {
            color: ${colors.darkgrey};
            box-shadow: ${boxShadowLight};
          }
        `
      case 'confirm':
        return css`
          background: ${colors.green} !important;
          color: white !important;
          &:hover {
            background: ${colors.green} !important;
            box-shadow: ${boxShadow};
            color: white !important;
          }
          &:focus {
            color: white !important;
            box-shadow: ${boxShadowLight};
          }
        `
      case 'delete':
        return css`
          background: Transparent;
          &:hover {
            background: ${colors.red};
            box-shadow: ${boxShadow};
            color: white;
          }
          &:focus {
            color: white;
            box-shadow: ${colors.red};
          }
        `
      case 'draft':
        return css`
          background: ${colors.orange};
          color: white;
          &:hover {
            background: ${colors.orange};
            box-shadow: ${boxShadow};
            color: white;
          }
          &:focus {
            color: white;
            box-shadow: ${boxShadowLight};
          }
        `
      case 'edit':
        return css`
          background: ${colors.successgreen} !important;
          &:hover {
            background: ${colors.successgreen} !important;
            box-shadow: ${boxShadow};
          }
          &:focus {
            box-shadow: ${boxShadowLight};
          }
        `
      case 'primary':
        return css`
          background: ${colors.pink} !important;
          color: white !important;
          &:hover {
            background: ${colors.pink} !important;
            box-shadow: ${boxShadow};
          }
          &:focus {
            box-shadow: ${boxShadowLight};
          }
        `
      case 'resend':
        return css`
          background: ${colors.grey} !important;
          color: ${$disabled ? colors.white : colors.black};
          &:hover {
            background: ${colors.lightgrey} !important;
            box-shadow: ${boxShadow};
          }
          &:focus {
            box-shadow: ${boxShadowLight};
          }
        `
      case 'success':
        return css`
          background: ${colors.successgreen} !important;
          color: white !important;
          &:hover {
            background: ${colors.successgreen} !important;
            box-shadow: ${boxShadow};
          }
          &:focus {
            box-shadow: ${boxShadowLight};
          }
        `
      default:
        return ''
    }
  }}
`

export default function OldButton({
  children,
  className,
  disabled,
  href,
  id,
  link,
  loading,
  onClick,
  style,
  target,
  text,
  type = 'edit'
}: OldButtonProps) {
  const isDesktop = isBrowser || isTablet

  const renderBody = () => {
    if (loading) return <Spinner />
    if (children) return children
    return text
  }

  return (
    <StyledButton
      $type={type}
      $disabled={disabled}
      className={className}
      component={link ? Link : null}
      disabled={disabled}
      href={href}
      id={id || null}
      onClick={onClick || null}
      position={isDesktop ? 'left' : 'default'}
      style={style}
      target={target}
      to={link || null}
    >
      {renderBody()}
    </StyledButton>
  )
}
